import { QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AnalyticsProvider } from 'use-analytics';
import { entryQueryClientConfig } from '@/Config/api/entryQueryClient.config';
import client from '@/Config/api/queryClient';
import { isAuthenticated as checkAuth, initializeFromSession } from '@/Config/auth/authStore';
import analytics from '@/Config/libs/analytics';
import { BootstrapProvider } from '@/Domain/components/App/BootstrapProvider';
import { PreviewProvider } from '@/Domain/components/PreviewProvider';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Toaster } from '@/Domain/components/Shared/Toast/Toast';
import { SocketProvider } from '@/Domain/components/SocketProvider';

type AuthInitState = 'pending' | 'ready';

/**
 * AuthGate: Checks authentication status and renders either
 * the authenticated app (with BootstrapProvider) or the
 * unauthenticated entry routes (login/register).
 *
 * On mount we attempt one session-based JWT refresh
 * (`/v1/api/auth/generate_jwt`) to recover from a page reload — the access
 * token only lives in memory, so it disappears on every full navigation, but
 * the Django session cookie may still be valid and can mint a fresh token.
 */
export function AuthGate(): JSX.Element {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => checkAuth());
  const [initState, setInitState] = useState<AuthInitState>(() =>
    checkAuth() ? 'ready' : 'pending',
  );

  useEffect(() => {
    if (initState === 'ready') return;
    let cancelled = false;
    void initializeFromSession().then((ok) => {
      if (cancelled) return;
      if (ok) setIsAuthenticated(true);
      setInitState('ready');
    });
    return () => {
      cancelled = true;
    };
  }, [initState]);

  const handleUnauthenticated = useCallback(() => {
    setIsAuthenticated(false);
  }, []);

  if (initState === 'pending') {
    // Hold render until the session refresh resolves — otherwise we'd flash
    // the login screen for users who hit the SPA with a valid session cookie.
    // Show a spinner (not a blank page) so a slow generate_jwt — e.g. a cold
    // backend instance right after a login/logout reload — reads as loading
    // rather than a frozen screen.
    return (
      <div
        data-testid="auth-gate-loading"
        className="flex items-center justify-center h-screen w-screen"
      >
        <Spinner className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // `/i/...` is the operator-app subtree — it requires a JWT. If the user
    // ends up here without auth (session expired mid-navigation, bootstrap
    // returned 401, scenario switch + cleared cookies on a deep link, etc.),
    // the Outlet below would still match the `i/...` route and render a
    // half-mounted operator UI with no path back to the login form. Push
    // them to `/` instead so LoginPage mounts and they can re-authenticate.
    if (location.pathname.startsWith('/i/')) {
      return <Navigate to="/" replace />;
    }
    return (
      <QueryClientProvider client={entryQueryClientConfig}>
        <AnalyticsProvider instance={analytics}>
          <PostHogProvider client={posthog}>
            <Toaster />
            <Outlet />
          </PostHogProvider>
        </AnalyticsProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={client}>
      <BootstrapProvider onUnauthenticated={handleUnauthenticated}>
        <SocketProvider>
          <AnalyticsProvider instance={analytics}>
            <PreviewProvider>
              <Toaster />
              <PostHogProvider client={posthog}>
                <Outlet />
              </PostHogProvider>
            </PreviewProvider>
          </AnalyticsProvider>
        </SocketProvider>
      </BootstrapProvider>
    </QueryClientProvider>
  );
}
